import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Card, Divider, Button, Tooltip, Row, Col, Alert, Form, Select, App, Input, } from 'antd';
import { TeamIcon } from 'components/CustomIcons';
import { txt } from 'libs/i18n';
import { abandonProjectsIn, deleteTeam, setManagementGroup, transferOwnership, setTeamExternalId, } from 'api/admin/teams-api';
import clsx from 'clsx';
import AbandonProjects from 'screens/DashboardScreen/components/AbandonProjects';
import { UserDeleteOutlined } from '@ant-design/icons';
import DeleteModal from 'components/DeleteModal';
import { fetchAllUsers } from 'api/admin/users-api';
import { useSession } from 'components/Authenticated';
import TransferForm from 'components/TransferForm';
import { fetchManagementGroupCredit } from 'api/admin/management-groups-api';
import VoiceprintsCard from './VoiceprintsCard';
import teamStyles from './style.module.less';
import styles from '../style.module.less';
import ExportTemplates from './ExportTemplates';
import CreditSettings from '../../components/CreditSettings';
const TeamProfile = ({ team, creditDelta, onOperation, onCreditChanged, managementGroups, onRefetchTeam, }) => {
    var _a;
    const { session } = useSession();
    const { message } = App.useApp();
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [abandonProjects, setAbandonProjects] = useState({
        enable: team.abandonProjectsIn !== null,
        inDays: team.abandonProjectsIn !== null ? Number(team.abandonProjectsIn.split('.')[0]) : null,
    });
    const [teamMembers, setTeamMembers] = useState('loading');
    const [availableGroupCredit, setAvailableGroupCredit] = useState('loading');
    const [externalId, setExternalId] = useState((_a = team.externalId) !== null && _a !== void 0 ? _a : '');
    const [externalIdLoading, setExternalIdLoading] = useState(false);
    const fetchTeamMembers = async () => {
        const users = await fetchAllUsers(session.connection, undefined, team.id);
        setTeamMembers(users.list
            .filter((user) => !user.isTeamOwner)
            .map((user) => ({ value: Number(user.id), label: user.email })));
    };
    useEffect(() => {
        const fetchGroupCredit = async () => {
            const groupCredit = await fetchManagementGroupCredit(session.connection, Number(team.managementGroupId));
            setAvailableGroupCredit(groupCredit);
        };
        void fetchGroupCredit();
    }, [creditDelta]);
    useEffect(() => {
        void fetchTeamMembers();
    }, []);
    const handleResetDeletion = async (resetValue) => {
        const result = await abandonProjectsIn(session.connection, team.id, resetValue);
        if (result.isSuccess()) {
            void message.success(txt('cancelAutomaticalDeletion'));
        }
        else {
            void message.error(txt('resetAutomaticalDeletionFail'));
        }
    };
    const handleAbandonProjectsChange = (changedValues, isReset) => {
        setAbandonProjects(changedValues);
        if (isReset === true) {
            void handleResetDeletion(changedValues);
        }
    };
    const handleAbandonProjects = async () => {
        if (abandonProjects.inDays === null) {
            void message.error(txt('missingDays'));
            return;
        }
        const result = await abandonProjectsIn(session.connection, team.id, abandonProjects);
        if (result.isSuccess()) {
            void message.success(txt('successAutomaticalDeletion'));
        }
        else {
            void message.error(txt('settingAutomaticalDeletionFail'));
        }
    };
    const handleGroupChange = async (groupId) => {
        const result = await setManagementGroup(session.connection, Number(team.id), groupId);
        if (result.isSuccess()) {
            void message.success(`${txt('groupSuccess')} ${result.get().managementGroupName}`, 2);
        }
        else if (result.err() === 'invalid-group') {
            void message.error(`${txt('invalidGroup')}`);
        }
        else {
            void message.error(`${txt('unknownError')}`);
        }
    };
    const getManagementGroupId = () => {
        if (managementGroups.length === 0) {
            return null;
        }
        const currentGroup = managementGroups.find((group) => group.groupName === team.managementGroupName);
        if (currentGroup === undefined) {
            return null;
        }
        return currentGroup.id;
    };
    const groupChangeAllowed = (session.login.user.teamId !== String(team.id));
    const handleDeleteTeam = async (teamId, email) => {
        const result = await deleteTeam(session.connection, teamId);
        if (result.isSuccess()) {
            setDeleteModalVisible(false);
            void message.success(`${txt('user')}${email} ${txt('hisHerTeam')}${txt('succesfullyDeleted')}`, 5);
        }
        else {
            void message.error(txt('failedToDelete'), 2);
        }
    };
    const handleTransfer = async (newOwnerId) => {
        if (newOwnerId !== undefined) {
            await transferOwnership(session.connection, team.id, newOwnerId);
        }
        void onRefetchTeam();
        void fetchTeamMembers();
    };
    const handleExternalIdSave = async () => {
        setExternalIdLoading(true);
        try {
            const result = await setTeamExternalId(session.connection, team.id, externalId);
            if (result.isSuccess()) {
                void message.success(txt('externalIdSaveSuccess'));
            }
            else {
                void message.error(txt('externalIdSaveFail'));
            }
        }
        finally {
            setExternalIdLoading(false);
        }
    };
    return (_jsxs(Card, { bordered: false, className: styles.card, title: (_jsxs("div", { className: teamStyles.cardHeader, children: [_jsxs("h2", { children: [_jsx(TeamIcon, {}), ' ', txt('teamSettings')] }), _jsx("span", { children: `${txt('teamOwner')}: ${team.ownerEmail} | Team id: ${team.id}` })] })), children: [teamMembers !== 'loading' && teamMembers.length > 0 ? (_jsx(TransferForm, { options: teamMembers, onHandleTransfer: handleTransfer, tooltipDescription: txt('confirmTransferAdmin'), title: txt('transferOwnership'), validatorText: txt('chooseMember') })) : null, _jsx(Alert, { type: "info", showIcon: true, className: teamStyles.alert, message: txt('teamSettingsInfo') }), session.login.hasClaim('admin:superadmin') ? (_jsx(Form, { wrapperCol: { sm: { offset: 7 } }, labelCol: { sm: { span: 5 } }, labelAlign: "left", className: styles.form, style: { marginBottom: 24 }, onFinish: handleExternalIdSave, children: _jsx(Form.Item, { label: txt('externalId'), style: { marginBottom: 0 }, children: _jsxs(Input.Group, { compact: true, children: [_jsx(Input, { value: externalId, onChange: (e) => setExternalId(e.target.value), disabled: externalIdLoading, style: { width: 'calc(100% - 100px)' } }), _jsx(Button, { type: "primary", htmlType: "submit", loading: externalIdLoading, style: { marginLeft: 8 }, children: txt('save') })] }) }) })) : null, session.login.hasClaim('admin:superadmin')
                && (_jsx(Form, { wrapperCol: { sm: { offset: 7 } }, labelCol: { sm: { span: 5 } }, labelAlign: "left", className: styles.form, children: _jsx(Form.Item, { label: txt('group'), children: session.login.hasClaim('admin:superadmin') ? (_jsx(Tooltip, { title: groupChangeAllowed ? null : txt('groupWarningBody'), children: _jsxs(Select, { defaultValue: getManagementGroupId(), onChange: handleGroupChange, disabled: !groupChangeAllowed, children: [_jsx(Select.Option, { value: null, children: txt('none') }), managementGroups.map((group) => (_jsx(Select.Option, { value: group.id, children: group.groupName }, group.id)))] }) })) : null }) })), _jsx("div", { className: styles.sectionName, children: txt('credit') }), _jsx(Divider, { className: styles.divider }), _jsx(CreditSettings, { creditDelta: creditDelta, remainingCredit: team.credit, onOperation: onOperation, onCreditDeltaChanged: onCreditChanged }), team.managementGroupId !== null && (_jsx(Row, { className: teamStyles.availableGroupCredit, children: _jsx(Col, { offset: 12, children: _jsx("span", { children: `${txt('groupCredit')}: ${availableGroupCredit}\xa0min` }) }) })), _jsx("div", { className: styles.sectionName, children: txt('projectDeletion') }), _jsx(Divider, { className: styles.divider }), _jsx(AbandonProjects, { value: abandonProjects, onChange: handleAbandonProjectsChange, hasInfoCircle: true, isTeamOwner: session.login.hasClaim('team:owner'), children: _jsx(Tooltip, { title: abandonProjects.enable === null || !abandonProjects.enable ? txt('enableDeletion') : null, children: _jsx(Button, { className: styles.saveButton, type: "primary", onClick: handleAbandonProjects, disabled: abandonProjects.enable === null || !abandonProjects.enable, children: txt('save') }) }) }), _jsx("div", { className: styles.sectionName, children: txt('speakerId') }), _jsx(Divider, { className: styles.divider }), _jsx(VoiceprintsCard, { teamId: team.id }), _jsx("div", { className: styles.sectionName, children: txt('exportTemplates') }), _jsx(Divider, { className: styles.divider }), _jsx(ExportTemplates, { teamId: team.id }), _jsx("div", { className: clsx(styles.sectionName, styles.sectionNameDanger), children: txt('deleteTeam') }), _jsx(Divider, { className: styles.divider }), _jsxs(Row, { children: [_jsx(Col, { span: 12, children: _jsxs(Button, { onClick: () => setDeleteModalVisible(true), children: [_jsx(UserDeleteOutlined, {}), txt('deleteTeam')] }) }), _jsx(DeleteModal, { visible: deleteModalVisible, onCancel: () => setDeleteModalVisible(false), email: team.ownerEmail, isTeamOwner: true, onDelete: () => handleDeleteTeam(team.id, team.ownerEmail), redirectTo: "/admin/users" })] })] }));
};
export default TeamProfile;
